//
// Copyright (c) 2025 Comcast
//

/*
 *  Copyright (c) 2025 Comcast
 */

import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import colors from 'components/utils/colors'

import ToggleSwitch from 'components/Shared/ToggleSwitch/Index'
import IntegerField from 'components/Shared/IntegerField/Index'
import Button, { StyledButton } from 'components/Shared/Button/Index'
import { Popover, PopoverTrigger, PopoverContent } from '@tiptap-ui/components/tiptap-ui-primitive/popover'
import { Card, CardBody } from '@tiptap-ui/components/tiptap-ui-primitive/card'

const MIN_ROWS = 1
const MIN_COLS = 1
const MAX_ROWS = 40
const MAX_COLS = 15
const DEFAULT_ROWS = 3
const DEFAULT_COLS = 3

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  background: ${props => props.$withGrayBg ? colors.bgSecondaryStripe : '#FFFFFF'};
  padding: 4px;
`

const ToggleFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  padding: 4px;
`

const ToggleLabel = styled.div`
  color: ${props => props.$isActive ? colors.secondary20 : colors.secondary60};
  cursor: pointer;
  user-select: none;
`

const DialogActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding-bottom: 4px;

  ${StyledButton} {
    padding: 0;
    min-width: 68px;
  }
`

function clampNumber (value, min, max, fallback) {
  const n = parseInt(value, 10)
  if (isNaN(n)) {
    return fallback
  }
  return Math.min(Math.max(n, min), max)
}

export const InsertTablePopover = React.forwardRef(function InsertTablePopover (
  { editor, disabled = false },
  ref,
) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [rows, setRows] = React.useState(DEFAULT_ROWS)
  const [cols, setCols] = React.useState(DEFAULT_COLS)
  const [withHeaderRow, setWithHeaderRow] = React.useState(false)
  const anchorRef = React.useRef(null)

  const reset = React.useCallback(() => {
    setRows(DEFAULT_ROWS)
    setCols(DEFAULT_COLS)
    setWithHeaderRow(false)
  }, [])

  const open = React.useCallback(() => {
    if (disabled) {
      return
    }
    setIsOpen(true)
  }, [disabled])
  const close = React.useCallback(() => {
    setIsOpen(false)
  }, [])

  React.useImperativeHandle(ref, () => ({ open, close }), [open, close])

  const handleCancel = React.useCallback(() => {
    reset()
    setIsOpen(false)
  }, [reset])

  const handleCreate = React.useCallback(() => {
    if (!editor) {
      return
    }
    const finalRows = clampNumber(rows, MIN_ROWS, MAX_ROWS, DEFAULT_ROWS)
    const finalCols = clampNumber(cols, MIN_COLS, MAX_COLS, DEFAULT_COLS)
    editor.chain().focus().insertTable({ rows: finalRows, cols: finalCols, withHeaderRow }).run()
    reset()
    setIsOpen(false)
  }, [rows, cols, withHeaderRow, editor, reset])

  const handleRowsChange = React.useCallback((val) => {
    if (val === null) {
      setRows(null)
      return
    }
    let n = val
    if (n > MAX_ROWS) n = MAX_ROWS
    if (n < MIN_ROWS) n = MIN_ROWS
    setRows(n)
  }, [])

  const handleColsChange = React.useCallback((val) => {
    if (val === null) {
      setCols(null)
      return
    }
    let n = val
    if (n > MAX_COLS) n = MAX_COLS
    if (n < MIN_COLS) n = MIN_COLS
    setCols(n)
  }, [])

  // we rely on IntegerField's internal focus management, no need to auto-focus

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span ref={anchorRef} />
      </PopoverTrigger>
      <PopoverContent aria-label='Insert table' sideOffset={20}>
        <Card>
          <CardBody>
            <CardContent>
              <FieldRow>
                Rows
                <IntegerField
                  value={rows}
                  onChange={handleRowsChange}
                  minNumber={MIN_ROWS}
                  disabled={disabled}
                  width={70}
                />
              </FieldRow>
              <FieldRow $withGrayBg>
                Columns
                <IntegerField
                  value={cols}
                  onChange={handleColsChange}
                  minNumber={MIN_COLS}
                  disabled={disabled}
                  width={70}
                />
              </FieldRow>
              <ToggleFieldRow>
                <ToggleSwitch
                  value={withHeaderRow}
                  onChange={e => setWithHeaderRow(e.target.checked)}
                />
                <ToggleLabel
                  $isActive={withHeaderRow}
                  onClick={() => setWithHeaderRow(!withHeaderRow)}
                >
                  Header row
                </ToggleLabel>
              </ToggleFieldRow>
              <DialogActions>
                <Button
                  type='button'
                  disabled={disabled || rows === null || cols === null}
                  onClick={handleCreate}
                >
                  Create
                </Button>
                <Button
                  type='button'
                  secondary
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </DialogActions>
            </CardContent>
          </CardBody>
        </Card>
      </PopoverContent>
    </Popover>
  )
})

InsertTablePopover.displayName = 'InsertTablePopover'

InsertTablePopover.propTypes = {
  editor: PropTypes.object,
  disabled: PropTypes.bool,
}

export default InsertTablePopover
